import Service from '@ember/service';

/**
 * Represents the state of a draft response for a review.
 */
export type ReviewDraft = {
  generatedReplyRating: 'BAD' | 'GOOD' | null;
  hasUserEditedResponse: boolean;
  isResponseGenerated: boolean;
  textareaValue: string;
};

/**
 * Service for caching draft responses per review within a session.
 *
 * This service stores draft response state (textarea content and related flags)
 * in memory, allowing users to switch between reviews without losing their
 * work-in-progress responses. Drafts persist for the application's lifetime
 * but are cleared when the user leaves the Reviews route or refreshes the page.
 *
 * @example
 * // Save a draft
 * this.reviewDraftCache.setDraft(reviewId, {
 *   textareaValue: 'My response...',
 *   hasUserEditedResponse: true,
 *   isResponseGenerated: false,
 *   generatedReplyRating: null,
 * });
 *
 * // Restore a draft
 * const draft = this.reviewDraftCache.getDraft(reviewId);
 */
export default class ReviewDraftCacheService extends Service {
  private drafts = new Map<string, ReviewDraft>();

  clearAllDrafts(): void {
    this.drafts.clear();
  }

  clearDraft(reviewId: string): void {
    this.drafts.delete(reviewId);
  }

  getDraft(reviewId: string): ReviewDraft | undefined {
    return this.drafts.get(reviewId);
  }

  hasDraft(reviewId: string): boolean {
    return this.drafts.has(reviewId);
  }

  setDraft(reviewId: string, draft: ReviewDraft): void {
    this.drafts.set(reviewId, draft);
  }
}

declare module '@ember/service' {
  interface Registry {
    'review-draft-cache': ReviewDraftCacheService;
  }
}
